import React, { useState, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Switch,
  useToast,
  useColorModeValue,
  Box,
  Text,
} from '@chakra-ui/react'
import { useCreateGallery } from 'queries/gallery.query'
import Dropzone from '../../settings/components/Dropzone'

export function CreateGalleryModal({ isOpen, onClose }) {
  const toast = useToast()
  const { mutate, isPending } = useCreateGallery()

  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [title, setTitle] = useState('')
  const [category, setCategory] = useState('none')
  const [customCategory, setCustomCategory] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [imageError, setImageError] = useState(null)

  useEffect(() => {
    if (!isOpen) {
      setTitle('')
      setCategory('none')
      setCustomCategory('')
      setIsActive(true)
      setImageFile(null)
      setImageError(null)
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview)
        setImagePreview(null)
      }
    }
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview)
      }
    }
  }, [isOpen])

  const handleFileChange = file => {
    setImageFile(file)
    setImageError(null)
    if (file) {
      setImagePreview(URL.createObjectURL(file))
    } else {
      setImagePreview(null)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!imageFile) {
      setImageError('An image file is required.')
      return
    }

    const formData = new FormData()
    formData.append('image', imageFile)
    const finalCategory = category === 'other' ? customCategory : (category === 'none' ? '' : category)
    formData.append('category', finalCategory)
    if (title) formData.append('title', title)
    formData.append('is_active', isActive ? 1 : 0)

    mutate(formData, {
      onSuccess: () => {
        toast({
          title: 'Gallery Image uploaded.',
          description: 'The new image has been added successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        })
        setTitle('')
        setCategory('none')
        setCustomCategory('')
        setIsActive(true)
        setImageFile(null)
        onClose()
      },
      onError: error => {
        const backendMessage = error.response?.data?.message || error.response?.data?.error || error?.message || 'An error occurred while uploading.'
        toast({
          title: 'Upload failed.',
          description: backendMessage,
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'top',
        })
      },
    })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Upload Gallery Image</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form id="create-gallery-form" onSubmit={handleSubmit}>
            <FormControl mb={4} isInvalid={!!imageError}>
              <FormLabel>Image *</FormLabel>
              <Dropzone
                w={{ base: '100%', '2xl': '100%' }}
                me='36px'
                maxH={{ base: '60%', lg: '50%', '2xl': '100%' }}
                minH={{ base: '60%', lg: '50%', '2xl': '100%' }}
                title='Upload Photo'
                preview={imagePreview}
                onFileChange={handleFileChange}
              />
            </FormControl>

            <FormControl mb={4}>
              <FormLabel>Title (Optional)</FormLabel>
              <Input
                placeholder="e.g. Live at Hamburg 2026"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </FormControl>

            <FormControl mb={4}>
              <FormLabel>Category</FormLabel>
              <Select value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="none">None</option>
                <option value="concert">Concert</option>
                <option value="cultural">Cultural</option>
                <option value="sports">Sports</option>
                <option value="other">Other</option>
              </Select>
            </FormControl>

            {category === 'other' && (
              <FormControl mb={4} isRequired>
                <FormLabel>Custom Category Name</FormLabel>
                <Input
                  placeholder="e.g. Exhibitions"
                  value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value)}
                />
              </FormControl>
            )}

            <FormControl display="flex" alignItems="center" mb={4}>
              <FormLabel htmlFor="is-active" mb="0">
                Active on Website
              </FormLabel>
              <Switch
                id="is-active"
                isChecked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                colorScheme="brand"
              />
            </FormControl>
          </form>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="brand" mr={3} type="submit" form="create-gallery-form" isLoading={isPending}>
            Upload
          </Button>
          <Button variant="ghost" onClick={onClose} isDisabled={isPending}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
